'use client'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'

const workExperience = [
	{
		period: 'Jul 2026 - Present',
		company: 'Telekom Rendszerintegráció Zrt.',
		role: 'Software Developer',
		description: 'Did development, support, and modernization work on transport management systems and related hardware for MÁV, and further developed in-house systems as well.',
		color: 'from-green-500 to-teal-500',
	},
	{
		period: 'Apr 2025 - Present',
		company: 'BMSZC Trefort',
		role: 'Transactional Platform Development Lead',
		description: 'Led backend development for banking and other platforms, including a custom IDP. Worked on transactions, authorization, database design, audit logging, and secure REST APIs.',
		color: 'from-emerald-500 to-teal-500',
	},
	{
		period: 'May 2025 - Aug 2025',
		company: 'Magyar Szív',
		role: 'Head Developer',
		description: 'Built the complete backend system, server infrastructure, website, and authentication dashboard from the ground up.',
		color: 'from-green-500 to-emerald-500'
	},
	{
		period: '2023 - Apr 2025',
		company: 'BME University',
		role: 'Project Lead Developer',
		description: 'Developed MTMT Scoring and Statistics systems for BME University.',
		color: 'from-teal-500 to-green-500'
	},
	{
		period: '2022 - 2023',
		company: 'Freelance',
		role: 'Full Stack Developer',
		description: 'Developed server infrastructures, configurations, and websites for various clients.',
		color: 'from-emerald-500 to-teal-500'
	}
]

const Experience = () => {
	const ref = useRef(null)
	const lineRef = useRef(null)
	const isInView = useInView(ref, { once: false, amount: 0.4 })
	const isLineInView = useInView(lineRef, { once: false, amount: 0.3 })

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: { staggerChildren: 0.3, delayChildren: 0.2 }
		}
	} as const

	const itemVariants = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }
		}
	} as const

	return (
		<section id="experience" className="py-24 md:py-32">
			<div className="section-container">
				<motion.div
					ref={ref}
					variants={containerVariants}
					initial="hidden"
					animate={isInView ? 'visible' : 'hidden'}
					className="space-y-16"
				>
					{/* Header */}
					<motion.div variants={itemVariants} className="text-center max-w-2xl mx-auto">
						<h2 className="text-3xl md:text-5xl font-bold mb-6">
							Work <span className="text-gradient">Experience</span>
						</h2>
						<p className="text-lg text-gray-600 dark:text-gray-400">
							My professional journey and collaborations.
						</p>
					</motion.div>

					{/* Timeline */}
					<div ref={lineRef} className="relative max-w-3xl mx-auto">
						{/* Line */}
						<div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-800 -translate-x-1/2">
							<motion.div
								className="w-full bg-linear-to-b from-green-500 to-emerald-500 origin-top"
								initial={{ height: 0 }}
								animate={isLineInView ? { height: '100%' } : { height: 0 }}
								transition={{ duration: 2, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
							/>
						</div>

						{/* Items */}
						<div className="space-y-12">
							{workExperience.map((job, index) => (
								<motion.div
									key={index}
									initial={{ opacity: 0, y: 40 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: false, amount: 0.5 }}
									transition={{ duration: 0.8, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
									className={`relative flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
								>
									{/* Dot */}
									<motion.div
										className="absolute left-8 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-linear-to-br from-green-500 to-emerald-500 ring-4 ring-white dark:ring-[#0a0a0a] z-10"
										initial={{ scale: 0 }}
										whileInView={{ scale: 1 }}
										viewport={{ once: false, amount: 0.5 }}
										transition={{ duration: 0.4, delay: index * 0.2 + 0.3 }}
									/>

									{/* Card */}
									<div className={`w-full md:w-[calc(50%-2rem)] ml-16 md:ml-0 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
										<motion.div
											className="glass-card p-6 rounded-2xl"
											whileHover={{ y: -2 }}
										>
											<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-linear-to-r ${job.color} text-white mb-4`}>
												{job.period}
											</span>
											<h3 className="text-xl font-bold mb-1">{job.company}</h3>
											<p className="text-green-600 dark:text-green-400 font-medium mb-3">{job.role}</p>
											<p className="text-sm text-gray-600 dark:text-gray-400">{job.description}</p>
										</motion.div>
									</div>
								</motion.div>
							))}
						</div>
					</div>
				</motion.div>
			</div>
		</section>
	)
}

export default Experience
